/**
 * Payment Solver — Solve for Payment Mode
 * 
 * Given a desired payment amount, loan amount, frequency, and start date,
 * find the nearest valid repayment discipline rate.
 * 
 * Constraints:
 *   - Absolute minimum rate floor: 9.00%
 *   - Preferred solve band: 10.00% to 15.00%
 *   - Hard target ceiling: 18.00%
 *   - Whole-year amortization targets: 1–35 years
 */

import { getPaymentsPerYear, type PaymentFrequency } from "@/lib/calculationEngine";

export interface SolveForPaymentInput {
  desiredPayment: number;
  loanAmount: number;       // total borrowed (loan + existing balance)
  paymentFrequency: PaymentFrequency;
}

export interface SolveForPaymentResult {
  solvedRate: number;           // decimal, e.g. 0.1197
  solvedTermYears: number;
  solvedTermCount: number;
  basePayment: number;          // amortized payment at solved rate
  extraPrincipal: number;       // desiredPayment - basePayment (≥0)
  estimatedPayoffPayments: number; // with extra principal applied
  estimatedFinalPayment: number;
  found: boolean;
}

function calcPayment(principal: number, periodicRate: number, n: number): number {
  if (periodicRate === 0) return principal / n;
  return principal * (periodicRate / (1 - Math.pow(1 + periodicRate, -n)));
}

/**
 * Estimate payoff with extra principal each period.
 * Returns { payments, finalPayment }.
 */
function estimatePayoffWithExtra(
  principal: number,
  periodicRate: number,
  basePayment: number,
  extraPrincipal: number,
  maxPayments: number = 1000,
): { payments: number; finalPayment: number } {
  let balance = principal;
  const totalPayment = basePayment + extraPrincipal;
  let count = 0;

  for (let i = 0; i < maxPayments; i++) {
    if (balance <= 0.01) break;
    const interest = balance * periodicRate;
    const principalPortion = totalPayment - interest;

    if (principalPortion <= 0) {
      // Payment doesn't even cover interest
      return { payments: maxPayments, finalPayment: totalPayment };
    }

    if (principalPortion >= balance) {
      // Final payment
      count++;
      return { payments: count, finalPayment: interest + balance };
    }

    balance -= principalPortion;
    count++;
  }

  return { payments: count, finalPayment: 0 };
}

export function solveForPayment(input: SolveForPaymentInput): SolveForPaymentResult {
  const { desiredPayment, loanAmount, paymentFrequency } = input;
  const ppy = getPaymentsPerYear(paymentFrequency);

  const ABS_MIN_RATE = 0.09;
  const PREFERRED_MIN = 0.10;
  const PREFERRED_MAX = 0.15;
  const HARD_CEILING = 0.18;
  const RATE_STEP = 0.0001; // 0.01% precision

  let bestDiff = Infinity;
  let bestRate = 0.10;
  let bestYears = 5;
  let bestTermCount = 60;
  let bestBasePayment = 0;

  // Phase 1: Search preferred band 10%–15%
  for (let years = 1; years <= 35; years++) {
    const termCount = Math.round(years * ppy);
    if (termCount < 1) continue;
    for (let rate = PREFERRED_MIN; rate <= PREFERRED_MAX + 0.00001; rate += RATE_STEP) {
      const periodicRate = rate / ppy;
      const payment = calcPayment(loanAmount, periodicRate, termCount);
      if (payment > desiredPayment) continue;
      const diff = desiredPayment - payment;
      if (diff < bestDiff) {
        bestDiff = diff;
        bestRate = rate;
        bestYears = years;
        bestTermCount = termCount;
        bestBasePayment = payment;
      }
    }
  }

  // Phase 2: If nothing in preferred band, expand to 9%–18%
  if (bestBasePayment === 0 || bestDiff > desiredPayment * 0.5) {
    for (let years = 1; years <= 35; years++) {
      const termCount = Math.round(years * ppy);
      if (termCount < 1) continue;
      for (let rate = ABS_MIN_RATE; rate <= HARD_CEILING + 0.00001; rate += RATE_STEP) {
        const periodicRate = rate / ppy;
        const payment = calcPayment(loanAmount, periodicRate, termCount);
        if (payment > desiredPayment) continue;
        const diff = desiredPayment - payment;
        if (diff < bestDiff) {
          bestDiff = diff;
          bestRate = rate;
          bestYears = years;
          bestTermCount = termCount;
          bestBasePayment = payment;
        }
      }
    }
  }

  // Fallback: find closest at minimum rate
  if (bestBasePayment === 0) {
    for (let years = 35; years >= 1; years--) {
      const termCount = Math.round(years * ppy);
      if (termCount < 1) continue;
      const periodicRate = ABS_MIN_RATE / ppy;
      const payment = calcPayment(loanAmount, periodicRate, termCount);
      if (payment <= desiredPayment || bestBasePayment === 0) {
        bestRate = ABS_MIN_RATE;
        bestYears = years;
        bestTermCount = termCount;
        bestBasePayment = payment;
        if (payment <= desiredPayment) break;
      }
    }
  }

  const extraPrincipal = Math.max(0, desiredPayment - bestBasePayment);
  const periodicRate = bestRate / ppy;

  const { payments: estPayments, finalPayment } = estimatePayoffWithExtra(
    loanAmount, periodicRate, bestBasePayment, extraPrincipal,
  );

  // Round rate to 2 decimal places for display
  const solvedRate = Math.round(bestRate * 10000) / 10000;

  return {
    solvedRate,
    solvedTermYears: bestYears,
    solvedTermCount: bestTermCount,
    basePayment: Math.round(bestBasePayment * 100) / 100,
    extraPrincipal: Math.round(extraPrincipal * 100) / 100,
    estimatedPayoffPayments: estPayments,
    estimatedFinalPayment: Math.round(finalPayment * 100) / 100,
    found: bestBasePayment > 0,
  };
}
